import { createAsyncThunk } from '@reduxjs/toolkit';
import { errMsg } from '../utils/errMsg';
import {
    setLikedTracks,
    appendLikedTracks,
    addLike,
    removeLike,
    updateLikedSet,
    updateNotifySet,
    setLoading,
    setError,
} from '../slices/likesSlice';
import { refreshAndRetry } from '../utils/refreshAndRetry';
import { entityId } from '../../utils/entityId';
import { track as track_ } from '../../utils/analytics';
import { emitNotifyToast } from '../../utils/notifyToast';

// ── Liked LOCAL-library file paths (on-device) ──────────────────────────
// Likes live in the cloud and never carry a filePath — it's machine-specific,
// so it has no place in the cross-device likes row. We remember it here instead
// (per install, in localStorage) and re-attach it when the likes list loads, so
// a track that exists on THIS machine plays from its own file rather than from a
// web source. Keyed by the like's identity (artist|||title), which is exactly
// the server's ON CONFLICT (user_id, type, artist, title) key. Desktop only —
// on web/mobile there is no local library and the map stays empty.
const LOCAL_PATHS_KEY = 'mixgoat:likedLocalPaths';
const likeKeyOf = (artist, title) =>
    `${String(artist || '').trim().toLowerCase()}|||${String(title || '').trim().toLowerCase()}`;

function readLocalPathMap() {
    try { return JSON.parse(localStorage.getItem(LOCAL_PATHS_KEY)) || {}; }
    catch { return {}; }
}
function writeLocalPathMap(map) {
    try { localStorage.setItem(LOCAL_PATHS_KEY, JSON.stringify(map)); } catch { /* quota/full — non-fatal */ }
}
function rememberLocalPath(artist, title, filePath) {
    if (!filePath) return;
    const map = readLocalPathMap();
    map[likeKeyOf(artist, title)] = filePath;
    writeLocalPathMap(map);
}
function forgetLocalPath(artist, title) {
    const map = readLocalPathMap();
    const k = likeKeyOf(artist, title);
    if (k in map) { delete map[k]; writeLocalPathMap(map); }
}

// Extract the on-disk path a like should be associated with. Covers both the
// Manage/Prepare rows (top-level `filePath`/`file`) and a like fired from the
// now-playing player, whose `musicSrc` is the local audio-server URL.
function localPathFromTrack(track) {
    if (!track) return null;
    if (track.filePath) return track.filePath;
    if (track.file && !/^(https?:|online:)/i.test(track.file)) return track.file;
    const m = String(track.musicSrc || '').match(/^https?:\/\/localhost:3069\/audio\?path=(.+)$/);
    if (m) { try { return decodeURIComponent(m[1]); } catch { return null; } }
    return null;
}

// Like-outcome notifications for LikeToastBridge (see components/LikeToastBridge.jsx).
export const LIKE_TOAST_EVENT = 'mg:like-toast';
function emitLikeToast(detail) {
    if (typeof window === 'undefined') return;
    try { window.dispatchEvent(new CustomEvent(LIKE_TOAST_EVENT, { detail })); } catch { /* non-DOM env */ }
}

// Normalize track fields to snake_case — single source of truth
const normalizeTrack = (t) => ({
    artist: t.artist,
    title: t.title,
    artwork: t.artwork || t.artworkUrl || t.image || null,
    preview_url: t.preview_url || t.previewUrl || t.sampleUrl || t.preview || null,
    youtube_id: t.youtube_id || t.youtubeId || null,
    soundcloud_url: t.soundcloud_url || t.soundcloudUrl || null,
    label: t.label || null,
    created_at: t.created_at || t.createdAt || null,
    // `track_public_id` is the library DB column (this is an ingestion
    // boundary). entityId() resolves the music-info id from any field shape:
    // track objects from search/feed surface it under `.id` (prepareForPublicApi
    // rewrites public_id → id). Without it, likes from those surfaces land in
    // the DB with track_public_id = null, defeating the lazy YT resolve in the
    // sidebar. Expose the same value as `.id` so downstream reads `.id`.
    track_public_id: entityId(t),
    id: entityId(t),
    // The user_likes row id (integer), kept only for reference. NEVER an
    // entity id — see fromLikeRow.
    likeId: t.likeId ?? null,
    // Notification-bell state on the user_likes row. Must be forwarded
    // explicitly — this normalizer strips unknown fields, and losing it here
    // silently empties likes.notifySet.
    notify: !!t.notify,
    artists: Array.isArray(t.artists) ? t.artists : null,
    // On-disk path for a liked LOCAL file (desktop). Set by likeTrack's
    // optimistic add and re-attached from localStorage on fetch; a live value
    // means "play from this file, not from the web".
    local_file_path: t.local_file_path || null,
});

// Ingestion boundary for a `user_likes` row (GET /likes). The row's `id` is the
// LIKE-ROW integer, not the track — and entityId() reads `.id` FIRST, so leaving
// it in place made every id-first path resolve the wrong track: opening a liked
// track sent the like-row id as the catalog id and the server rendered whatever
// track happens to own that internal id (like #618 "Sub Oslo - Goatsucker Dub"
// opened track 618, "Santone - Higher" — which then also read as not-liked). It
// equally poisoned lazy YT resolve (`tid=`) and the catalog re-hydrate below.
// Drop it here, keep it under `likeId`, and let entityId() fall through to
// track_public_id — exactly what the liked-MIXES thunk already does.
export const fromLikeRow = ({ id, ...row }) => normalizeTrack({ ...row, likeId: id ?? null });

// A real catalog id is a base62 public_id STRING. Numbers and all-digit
// strings are foreign row ids (local SQLite rowid, user_playlist_tracks
// serial, user_likes serial) — never write one as a catalog id.
export const asCatalogId = (v) =>
    (typeof v === 'string' && v && !/^\d+$/.test(v) ? v : null);

// Hydrate likes with the latest catalog data (artists[] + playback fields) by
// batching a tracksByPublicIds lookup. Older saved likes often miss
// youtube_id / soundcloud_url because they weren't populated at like-time;
// pulling them fresh here is what keeps the row playable from the likes view.
// Likes lacking a public id (non-catalog tracks) are skipped.
async function hydrateLikesWithArtists(rows) {
    // String() — legacy rows stored numeric public ids; the server schema
    // wants strings, and one numeric id used to 400 the whole batch.
    const ids = [...new Set(rows.map(r => r.track_public_id).filter(Boolean).map(String))];
    if (!ids.length) return rows;

    let token;
    try {
        token = localStorage.getItem('djpal:token');
        if (!token) return rows;
    } catch { return rows; }

    try {
        const res = await window.electron.djpal.tracksByPublicIds(token, ids);
        if (!res || res.error || !Array.isArray(res)) return rows;

        // The public API response sanitizer rewrites public_id → id, so the
        // id we keyed the request on comes back as `t.id`.
        const byId = new Map();
        for (const t of res) {
            const pid = entityId(t);
            if (pid) byId.set(pid, t);
        }
        return rows.map(r => {
            if (!r.track_public_id) return r;
            const hit = byId.get(r.track_public_id);
            if (!hit) return r;
            return {
                ...r,
                artists: Array.isArray(hit.artists) ? hit.artists : r.artists,
                preview_url: hit.sampleUrl || hit.previewUrl || r.preview_url || null,
                youtube_id: hit.youtubeId || r.youtube_id || null,
                soundcloud_url: hit.soundcloudUrl || r.soundcloud_url || null,
                artwork: hit.artworkUrl || r.artwork || null,
                duration: hit.duration ?? r.duration ?? null,
            };
        });
    } catch (err) {
        console.warn('hydrateLikesWithArtists failed:', err.message);
        return rows;
    }
}

// Re-attach on-disk paths (captured at like-time) to liked LOCAL-library tracks
// and drop any whose file no longer exists. A row that comes out of here with a
// live `local_file_path` is played from that file — never from a web source.
// Desktop only; a no-op where the local library IPC isn't present (web/mobile).
async function hydrateLikesWithLocalFiles(rows) {
    const checkMissing = window.electron?.db?.tracks?.checkMissingFiles;
    if (typeof checkMissing !== 'function') return rows;

    const map = readLocalPathMap();
    const withPath = rows.map(r => {
        const p = r.local_file_path || map[likeKeyOf(r.artist, r.title)] || null;
        return p ? { ...r, local_file_path: p } : r;
    });

    const paths = [...new Set(withPath.map(r => r.local_file_path).filter(Boolean))];
    if (!paths.length) return withPath;

    let missing = new Set();
    try {
        const res = await checkMissing(paths);
        if (Array.isArray(res)) missing = new Set(res);
    } catch (err) {
        // On check failure, keep the paths — a stale path only costs a 404 at
        // play time, whereas dropping them all would needlessly fall back to web.
        console.warn('hydrateLikesWithLocalFiles existence check failed:', err.message);
    }
    return withPath.map(r =>
        r.local_file_path && missing.has(r.local_file_path)
            ? { ...r, local_file_path: null }
            : r
    );
}

export const fetchLikes = createAsyncThunk(
    'likes/fetchLikes',
    async ({ page = 1, limit = 50, append = false } = {}, { dispatch }) => {
        try {
            dispatch(setLoading(true));

            const result = await refreshAndRetry(
                (token) => window.electron.library.getLikes(token, page, limit)
            );

            if (result.error) {
                dispatch(setError(errMsg(result.error)));
                return;
            }

            const rawData = (result.data || []).map(fromLikeRow);
            const total = result.pagination?.total || 0;
            const data = await hydrateLikesWithLocalFiles(await hydrateLikesWithArtists(rawData));

            if (append) {
                dispatch(appendLikedTracks({ data, total }));
            } else {
                dispatch(setLikedTracks({ data, total }));
            }

            const setUpdate = {};
            const notifyUpdate = {};
            for (const track of data) {
                setUpdate[`${track.artist}|||${track.title}`] = true;
                notifyUpdate[`${track.artist}|||${track.title}`] = !!track.notify;
            }
            dispatch(updateLikedSet(setUpdate));
            dispatch(updateNotifySet(notifyUpdate));
        } catch (error) {
            console.error('Error fetching likes:', error);
            dispatch(setError(errMsg(error)));
        } finally {
            dispatch(setLoading(false));
        }
    }
);

export const likeTrack = createAsyncThunk(
    'likes/likeTrack',
    async (rawTrack, { dispatch }) => {
        // `likeSource` describes WHERE the like came from (lock screen, feed
        // row, …). It is analytics metadata, not track data — strip it here
        // so it can never reach the stored like row.
        const { likeSource, ...track } = rawTrack || {};
        try {
            // Remember where this file lives on disk (if it's a local track) so
            // the likes list can play it from the file instead of the web.
            const localPath = localPathFromTrack(track);
            if (localPath) rememberLocalPath(track.artist, track.title, localPath);

            // Optimistic update
            dispatch(addLike(normalizeTrack({ ...track, local_file_path: localPath, created_at: new Date().toISOString() })));

            // "<Title> was saved to your Likes." card — fired optimistically
            // alongside the heart filling in so it feels instant. Thunks live
            // outside React, so this rides a window event that
            // LikeToastBridge (mounted inside ToastProvider) turns into a
            // toast; a failed save below undoes the like and tells the user.
            emitLikeToast({
                status: 'liked',
                likeSource: likeSource || null,
                artist: track.artist,
                title: track.title,
                artwork: track.artwork || track.artworkUrl || track.image || track.entityArtwork
                    || (localPath ? `http://localhost:3069/album-art?path=${encodeURIComponent(localPath)}` : null),
            });

            // music-info-server catalog id. Several row shapes reach this thunk
            // carrying a FOREIGN integer in `.id` — the local SQLite rowid
            // (Library tabs) and the user_playlist_tracks serial (cloud
            // playlists; 2026-08-06 stored rows 5741/5742 as catalog ids and
            // rendered someone else's track page). A like without an id is
            // merely un-hydrated; a like with a wrong id is poisoned.
            const catalogId =
                asCatalogId(track.djpalId || track.djpal_id) ||
                (localPath ? null : asCatalogId(entityId(track)));

            const result = await refreshAndRetry(
                (token) => window.electron.library.likeTrack(token, {
                    artist: track.artist,
                    title: track.title,
                    artwork: track.artwork || track.artworkUrl || track.image || null,
                    previewUrl: track.previewUrl || track.sampleUrl || track.preview_url || track.preview || null,
                    // snake_case fallbacks: rows from a like/cloud-playlist
                    // surface carry youtube_id / soundcloud_url; without these
                    // the stored like had NULL links and no play icon.
                    youtubeId: track.youtubeId || track.youtube_id || null,
                    soundcloudUrl: track.soundcloudUrl || track.soundcloud_url || null,
                    id: catalogId,
                })
            );

            if (result.error) {
                dispatch(removeLike({ artist: track.artist, title: track.title }));
                emitLikeToast({ status: 'failed', artist: track.artist, title: track.title });
                console.error('Like failed:', result.error);
            } else {
                track_('track_liked', {
                    artist: track.artist,
                    title: track.title,
                    track_id: catalogId || undefined,
                    is_local: !!localPath,
                    from: likeSource || undefined,
                });
            }
        } catch (error) {
            dispatch(removeLike({ artist: track.artist, title: track.title }));
            emitLikeToast({ status: 'failed', artist: track.artist, title: track.title });
            console.error('Error liking track:', error);
        }
    }
);

export const unlikeTrack = createAsyncThunk(
    'likes/unlikeTrack',
    async ({ artist, title, likeSource }, { dispatch, getState }) => {
        try {
            // Snapshot the like row BEFORE the optimistic remove: the
            // "was removed from your Likes — Undo" card re-likes from it
            // (artwork, catalog id, play links, on-disk path all preserved).
            const key = likeKeyOf(artist, title);
            const row = (getState().likes?.likedTracks || []).find(
                (t) => likeKeyOf(t.artist, t.title) === key
            ) || null;
            const localPath = row?.local_file_path || readLocalPathMap()[key] || null;

            // Optimistic update
            dispatch(removeLike({ artist, title }));
            forgetLocalPath(artist, title);

            emitLikeToast({
                status: 'unliked',
                likeSource: likeSource || null,
                artist,
                title,
                artwork: row?.artwork
                    || (localPath ? `http://localhost:3069/album-art?path=${encodeURIComponent(localPath)}` : null),
                // Everything likeTrack needs to restore the like as it was.
                undoTrack: { ...(row || { artist, title }), ...(localPath ? { filePath: localPath } : {}) },
            });

            const result = await refreshAndRetry(
                (token) => window.electron.library.unlikeTrack(token, artist, title)
            );

            if (result.error) {
                console.error('Unlike failed:', result.error);
            } else {
                track_('track_unliked', { artist, title, from: likeSource || undefined });
            }
        } catch (error) {
            console.error('Error unliking track:', error);
        }
    }
);

export const checkLikes = createAsyncThunk(
    'likes/checkLikes',
    async (tracks, { dispatch }) => {
        try {
            const result = await refreshAndRetry(
                (token) => window.electron.library.checkLikes(token, tracks)
            );

            if (result.error) {
                console.error('Check likes failed:', result.error);
                return;
            }

            const data = result.data || [];
            const setUpdate = {};
            const notifyUpdate = {};
            tracks.forEach((track, i) => {
                const key = `${track.artist}|||${track.title}`;
                // New servers answer { liked, notify } (we send withNotify);
                // an old server during the rollout window still answers bare
                // booleans — handle both.
                const entry = data[i];
                if (entry && typeof entry === 'object') {
                    setUpdate[key] = !!entry.liked;
                    notifyUpdate[key] = !!entry.notify;
                } else {
                    setUpdate[key] = !!entry;
                }
            });
            dispatch(updateLikedSet(setUpdate));
            dispatch(updateNotifySet(notifyUpdate));
        } catch (error) {
            console.error('Error checking likes:', error);
        }
    }
);

export const setTrackNotify = createAsyncThunk(
    'likes/setTrackNotify',
    async ({ artist, title, notify, from, artwork, quiet }, { dispatch, getState }) => {
        const key = `${artist}|||${title}`;
        // Confirmation card for ConfirmationToastBridge (see setArtistNotify);
        // artwork falls back to the loaded like row.
        const row = (getState().likes?.likedTracks || []).find((t) => t.artist === artist && t.title === title);
        const toast = { entityType: 'track', name: title, artist, title, artwork: artwork || row?.artwork || null, notify, quiet: !!quiet };
        try {
            // Optimistic toggle, reverted on failure (same pattern as like).
            dispatch(updateNotifySet({ [key]: notify }));
            emitNotifyToast({ status: 'ok', ...toast });

            const result = await refreshAndRetry(
                (token) => window.electron.library.setTrackNotify(token, artist, title, notify)
            );

            if (result.error) {
                dispatch(updateNotifySet({ [key]: !notify }));
                emitNotifyToast({ status: 'failed', ...toast });
                console.error('Set track notify failed:', result.error);
            } else {
                track_('track_notify_toggled', {
                    artist,
                    title,
                    notify,
                    from: from || 'button',
                });
            }
        } catch (error) {
            dispatch(updateNotifySet({ [key]: !notify }));
            console.error('Error setting track notify:', error);
        }
    }
);
